import React from 'react';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Field,
  MiniSelect,
  NumberInput,
  BiometryList,
  MorphologyList,
  InlineSelect,
  SectionHeading,
  TwoColumnLayout,
  ScreeningSection,
  FetalAnatomySection,
  PercentileBar,
  styles,
} from './primitives';
import {
  T2_BIOMETRY,
  T2_BRAIN_MEASUREMENTS,
  T2_MORPHOLOGY,
  OPTIONS,
  amnioticFluidFromSdpCm,
  baselineFhrFromHeartRateBpm,
} from './lib/form-config';
import { useReportData } from './lib/report-context';

function numVal(m: Record<string, unknown>, key: string): number | undefined {
  const v = m[key];
  if (v == null) {
    return undefined;
  }
  const n = Number(v);
  return isNaN(n) ? undefined : n;
}

function strVal(m: Record<string, unknown>, key: string): string {
  const v = m[key];
  if (v == null) {
    return '';
  }
  return String(v);
}

function blurHandler(
  key: string,
  onUpdate?: (u: Record<string, unknown>) => void
) {
  return (e: React.FocusEvent<HTMLInputElement>) =>
    onUpdate?.({ [key]: e.target.value ? parseFloat(e.target.value) : null });
}

function PresentationVitality() {
  const { fetalAssessment, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const hr = fetalAssessment.heartRate
    ? Math.round(fetalAssessment.heartRate)
    : undefined;
  const baselineFhr = baselineFhrFromHeartRateBpm(hr);

  return (
    <>
      <SectionHeading>sections.presentationVitality</SectionHeading>
      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
        <Field
          label="fields.fetalPresentation"
          explainFindingKey="presentation"
        >
          <MiniSelect
            defaultValue={fetalAssessment?.presentation || ''}
            placeholder="placeholders.selectPresentation"
            options={OPTIONS.fetalPresentation}
            onChange={(e) =>
              onUpdateFindings?.({
                presentation: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.facing" explainFindingKey="presentation">
          <MiniSelect
            defaultValue={fetalAssessment?.facing || ''}
            placeholder="placeholders.selectPresentation"
            options={OPTIONS.facing}
            onChange={(e) =>
              onUpdateFindings?.({
                facing: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.spineOrientation">
          <MiniSelect
            defaultValue={(sr.spine_orientation as string) || ''}
            placeholder="placeholders.selectSpineOrientation"
            options={[
              { value: 'left', label: 'options.left' },
              { value: 'right', label: 'options.right' },
              { value: 'anterior', label: 'options.anterior' },
              { value: 'posterior', label: 'options.posterior' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ spine_orientation: e.target.value || null })
            }
          />
        </Field>
        <Field
          label="fields.cardiacActivity"
          explainFindingKey="cardiac_activity"
        >
          <MiniSelect
            defaultValue={
              sr.cardiac_activity_present === true
                ? 'present-regular'
                : sr.cardiac_activity_present === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.cardiacActivity}
            onChange={(e) =>
              onUpdateFindings?.({
                cardiac_activity_present:
                  e.target.value === 'absent' ? false : !!e.target.value,
              })
            }
          />
        </Field>
        <Field label="fields.heartRate" explainFindingKey="cardiac_activity">
          <NumberInput
            defaultValue={hr}
            step={1}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                heart_rate: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="Baseline FHR" explainFindingKey="cardiac_activity">
          <MiniSelect
            value={baselineFhr || ''}
            placeholder="Baseline FHR"
            options={OPTIONS.baselineFhr}
            disabled
          />
        </Field>
        <Field label="fields.movementPresent">
          <MiniSelect
            defaultValue={
              sr.fetal_movement_present === true
                ? 'yes'
                : sr.fetal_movement_present === false
                  ? 'no'
                  : ''
            }
            options={OPTIONS.yesNo}
            onChange={(e) =>
              onUpdateFindings?.({
                fetal_movement_present: e.target.value === 'yes',
              })
            }
          />
        </Field>
        <Field label="fields.movementQuality">
          <MiniSelect
            defaultValue={(sr.fetal_movement_quality as string) || ''}
            options={[
              { value: 'active', label: 'options.active' },
              { value: 'reduced', label: 'options.reduced' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                fetal_movement_quality: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function BiometrySection() {
  const { measurementValues, onUpdateFindings } = useReportData();
  const refChart =
    measurementValues.reference_chart != null
      ? String(measurementValues.reference_chart)
      : '';

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <SectionHeading>sections.biometry</SectionHeading>
        <MiniSelect
          defaultValue={refChart || 'intergrowth-21st'}
          options={OPTIONS.referenceChart}
          className="w-44 shrink-0"
          onChange={(e) =>
            onUpdateFindings?.({ reference_chart: e.target.value || null })
          }
        />
      </div>
      <BiometryList fields={T2_BIOMETRY} />
    </>
  );
}

function BrainMeasurements() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.brainMeasurements</SectionHeading>
      <BiometryList fields={T2_BRAIN_MEASUREMENTS} />
      <InlineSelect
        label="fields.corpusCallosum"
        defaultValue={strVal(m, 'corpus_callosum') || ''}
        options={[
          { value: 'present', label: 'options.present' },
          { value: 'short', label: 'options.shortPartialAcc' },
          { value: 'absent', label: 'options.absentAcc' },
          { value: 'not-seen', label: 'options.notVisualized' },
        ]}
        onChange={(e) =>
          onUpdateFindings?.({
            corpus_callosum: e.target.value || null,
          })
        }
      />
      <InlineSelect
        label="fields.sylvianFissure"
        defaultValue={(sr.sylvian_fissure as string) || ''}
        options={[
          { value: 'normal', label: 'options.normalOpercularization' },
          { value: 'delayed', label: 'options.delayedAbsent' },
          { value: 'not-seen', label: 'options.notVisualized' },
        ]}
        onChange={(e) =>
          onUpdateFindings?.({ sylvian_fissure: e.target.value || null })
        }
      />
    </>
  );
}

function calcEfwHadlockI(ac: number, fl: number): number {
  return Math.pow(10, 1.304 + 0.05281 * ac + 0.1938 * fl - 0.004 * ac * fl);
}
function calcEfwHadlockII(bpd: number, ac: number, fl: number): number {
  return Math.pow(
    10,
    1.335 - 0.0034 * ac * fl + 0.0316 * bpd + 0.0457 * ac + 0.1623 * fl
  );
}
function calcEfwHadlockIII(hc: number, ac: number, fl: number): number {
  return Math.pow(
    10,
    1.326 - 0.00326 * ac * fl + 0.0107 * hc + 0.0438 * ac + 0.158 * fl
  );
}
function calcEfwHadlockIV(
  bpd: number,
  hc: number,
  ac: number,
  fl: number
): number {
  return Math.pow(
    10,
    1.3596 -
      0.00386 * ac * fl +
      0.0064 * hc +
      0.00061 * bpd * ac +
      0.0424 * ac +
      0.174 * fl
  );
}
function calcEfwIntergrowth(ac: number, fl: number): number {
  return Math.exp(
    5.08482 -
      54.06633 * Math.pow(ac / 100, 3) -
      95.80076 * Math.pow(fl / 100, 3) +
      4.814601 * (ac / 100) +
      3.448628 * (fl / 100)
  );
}

type EfwResults = {
  'hadlock-i'?: number;
  'hadlock-ii'?: number;
  'hadlock-iii'?: number;
  'hadlock-iv'?: number;
  intergrowth?: number;
};

function computeAllEfw(
  bpd?: number,
  hc?: number,
  ac?: number,
  fl?: number
): EfwResults {
  const r: EfwResults = {};
  if (ac !== undefined && fl !== undefined) {
    r['hadlock-i'] = Math.round(calcEfwHadlockI(ac, fl));
    r['intergrowth'] = Math.round(calcEfwIntergrowth(ac, fl));
  }
  if (bpd !== undefined && ac !== undefined && fl !== undefined) {
    r['hadlock-ii'] = Math.round(calcEfwHadlockII(bpd, ac, fl));
  }
  if (hc !== undefined && ac !== undefined && fl !== undefined) {
    r['hadlock-iii'] = Math.round(calcEfwHadlockIII(hc, ac, fl));
  }
  if (
    bpd !== undefined &&
    hc !== undefined &&
    ac !== undefined &&
    fl !== undefined
  ) {
    r['hadlock-iv'] = Math.round(calcEfwHadlockIV(bpd, hc, ac, fl));
  }
  return r;
}

function normCdf(z: number): number {
  const sign = z >= 0 ? 1 : -1;
  const az = Math.abs(z);
  const t = 1 / (1 + 0.3275911 * az);
  const poly =
    t *
    (0.254829592 +
      t *
        (-0.284496736 +
          t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  return 0.5 * (1 + sign * (1 - poly * Math.exp((-az * az) / 2)));
}

function computeEfwPercentile(
  efwG: number,
  gaWeeks: number,
  referenceChart: string
): number | undefined {
  if (efwG <= 0 || gaWeeks < 14 || gaWeeks > 43) {
    return undefined;
  }
  let meanLog10: number;
  let sdLog10: number;
  if (referenceChart.includes('intergrowth')) {
    meanLog10 = 0.5934 + 0.1169 * gaWeeks - 0.001078 * gaWeeks ** 2;
    sdLog10 = 0.06;
  } else {
    meanLog10 = -0.058 + 0.1674 * gaWeeks - 0.001925 * gaWeeks ** 2;
    sdLog10 = 0.061;
  }
  const z = (Math.log10(efwG) - meanLog10) / sdLog10;
  return Math.round(normCdf(z) * 100 * 10) / 10;
}

const EFW_FORMULA_OPTIONS = [
  { value: 'hadlock-i', label: 'options.hadlockI' },
  { value: 'hadlock-ii', label: 'options.hadlockII' },
  { value: 'hadlock-iii', label: 'options.hadlockIII' },
  { value: 'hadlock-iv', label: 'options.hadlockIV' },
  { value: 'intergrowth', label: 'options.intergrowthAcFl' },
] as const;

function EFWSection() {
  const { t } = useTranslation('Report');
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  const bpd = numVal(m, 'bpd_cm');
  const hc = numVal(m, 'hc_cm');
  const ac = numVal(m, 'ac_cm');
  const fl = numVal(m, 'fl_cm');
  const gaWeeks = numVal(m, 'ga_aua');

  const flAcRatio = useMemo(() => {
    if (fl === undefined || ac === undefined || ac <= 0) {
      return undefined;
    }
    return Math.round((fl / ac) * 10000) / 10000;
  }, [fl, ac]);

  const efwResults = useMemo(
    () => computeAllEfw(bpd, hc, ac, fl),
    [bpd, hc, ac, fl]
  );

  const initialFormula = (strVal(m, 'efw_formula') ||
    'hadlock-iv') as keyof EfwResults;
  const [selectedFormula, setSelectedFormula] =
    useState<keyof EfwResults>(initialFormula);

  const [efwRefChart, setEfwRefChart] = useState<string>(
    strVal(m, 'efw_reference_chart') || 'intergrowth'
  );

  const onUpdateFindingsRef = useRef(onUpdateFindings);
  useEffect(() => {
    onUpdateFindingsRef.current = onUpdateFindings;
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    if (Object.keys(efwResults).length === 0 && flAcRatio === undefined) {
      return;
    }
    const updates: Record<string, number | null> = {
      efw_hadlock_i_g: efwResults['hadlock-i'] ?? null,
      efw_hadlock_ii_g: efwResults['hadlock-ii'] ?? null,
      efw_hadlock_iii_g: efwResults['hadlock-iii'] ?? null,
      efw_hadlock_iv_g: efwResults['hadlock-iv'] ?? null,
      efw_who_2017_g: efwResults['intergrowth'] ?? null,
      fl_ac_ratio: flAcRatio ?? null,
    };
    const activeEfw = efwResults[selectedFormula];
    if (activeEfw !== undefined) {
      updates['efw_g'] = activeEfw;
    }
    onUpdateFindingsRef.current?.(updates);
  }, [efwResults, flAcRatio]); // eslint-disable-line react-hooks/exhaustive-deps

  const efwPercentile = useMemo(() => {
    const efw = efwResults[selectedFormula];
    if (efw === undefined || gaWeeks === undefined) {
      return undefined;
    }
    return computeEfwPercentile(efw, gaWeeks, efwRefChart);
  }, [efwResults, selectedFormula, gaWeeks, efwRefChart]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (efwPercentile !== undefined) {
      onUpdateFindingsRef.current?.({ efw_percentile: efwPercentile });
    }
  }, [efwPercentile]);

  const efwDisplayValue = efwResults[selectedFormula] ?? numVal(m, 'efw_g');

  const handleFormulaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const formula = e.target.value as keyof EfwResults;
    setSelectedFormula(formula);
    const newEfw = efwResults[formula] ?? numVal(m, 'efw_g');
    onUpdateFindings?.({ efw_formula: formula, efw_g: newEfw ?? null });
  };

  return (
    <>
      <SectionHeading>sections.estimatedFetalWeight</SectionHeading>

      <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 mb-1">
        {EFW_FORMULA_OPTIONS.map(({ value, label }) => {
          const val = efwResults[value as keyof EfwResults];
          return (
            <div key={value} className="flex items-center gap-1">
              <span
                className={`${styles.label} truncate flex-1`}
                title={t(label)}
              >
                {t(label)}
              </span>
              <span className="text-xs font-mono text-slate-700 dark:text-gray-200 w-14 text-right">
                {val !== undefined ? `${val} g` : '—'}
              </span>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-4 gap-1">
        <Field label="fields.efwG">
          <NumberInput
            key={`${selectedFormula}-${efwDisplayValue}`}
            placeholder="g"
            step={1}
            defaultValue={efwDisplayValue}
            onBlur={blurHandler('efw_g', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.formula">
          <MiniSelect
            value={selectedFormula}
            options={EFW_FORMULA_OPTIONS}
            onChange={handleFormulaChange}
          />
        </Field>
        <Field label="fields.reference">
          <MiniSelect
            defaultValue={strVal(m, 'efw_reference_chart') || 'intergrowth'}
            options={[
              { value: 'intergrowth', label: 'options.intergrowth' },
              { value: 'hadlock', label: 'options.hadlock' },
            ]}
            onChange={(e) => {
              const chart = e.target.value || 'intergrowth';
              setEfwRefChart(chart);
              onUpdateFindings?.({ efw_reference_chart: chart });
            }}
          />
        </Field>
        <Field label="fields.efwPercentile">
          <PercentileBar
            percentile={efwPercentile ?? numVal(m, 'efw_percentile')}
          />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.flAcRatio">
          {(() => {
            const ratio = flAcRatio ?? numVal(m, 'fl_ac_ratio');
            const isNormal =
              ratio !== undefined && ratio >= 0.2 && ratio <= 0.24;
            const isLow = ratio !== undefined && ratio < 0.2;
            const color = isNormal
              ? 'text-emerald-600 dark:text-emerald-400'
              : isLow
                ? 'text-red-500 dark:text-red-400'
                : ratio !== undefined
                  ? 'text-amber-500 dark:text-amber-400'
                  : 'text-slate-400 dark:text-gray-500';
            return (
              <div className="h-7 flex items-center px-1.5">
                <span className={`text-sm font-mono font-semibold ${color}`}>
                  {ratio !== undefined ? ratio.toFixed(4) : '—'}
                </span>
                <span className="ml-1 text-sm text-slate-400 dark:text-gray-500">
                  (0.20–0.24)
                </span>
              </div>
            );
          })()}
        </Field>
        <Field label="fields.handDigits">
          <MiniSelect
            defaultValue={(sr.hand_digits as string) || ''}
            options={[
              { value: 'normal', label: 'options.fiveDigitsNormal' },
              { value: 'polydactyly', label: 'options.polydactyly' },
              { value: 'overlapping', label: 'options.overlappingT18' },
              { value: 'split', label: 'options.splitHand' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ hand_digits: e.target.value || null })
            }
          />
        </Field>
      </div>
    </>
  );
}

function DopplerSections() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;
  const { t } = useTranslation('Report');

  return (
    <>
      <SectionHeading>sections.umbilicalArteryDoppler</SectionHeading>
      <div className="grid grid-cols-4 gap-1">
        <Field label="fields.pi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'ua_pi')}
            onBlur={blurHandler('ua_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.ri">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'ua_ri')}
            onBlur={blurHandler('ua_ri', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.percentile">
          <NumberInput
            step={1}
            defaultValue={numVal(m, 'ua_pi_percentile')}
            onBlur={blurHandler('ua_pi_percentile', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.diastolicFlow">
          <MiniSelect
            defaultValue={(sr.ua_diastolic_flow as string) || ''}
            options={OPTIONS.diastolicFlow}
            onChange={(e) =>
              onUpdateFindings?.({ ua_diastolic_flow: e.target.value || null })
            }
          />
        </Field>
      </div>

      <SectionHeading>sections.uterineArteryDoppler</SectionHeading>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.rightPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'uta_right_pi')}
            onBlur={blurHandler('uta_right_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.leftPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'uta_left_pi')}
            onBlur={blurHandler('uta_left_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.meanPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'uta_mean_pi')}
            onBlur={blurHandler('uta_mean_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.rightNotch">
          <MiniSelect
            defaultValue={
              sr.uta_right_notch === true
                ? 'present'
                : sr.uta_right_notch === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.funneling}
            onChange={(e) =>
              onUpdateFindings?.({
                uta_right_notch: e.target.value === 'present' ? true : false,
              })
            }
          />
        </Field>
        <Field label="fields.leftNotch">
          <MiniSelect
            defaultValue={
              sr.uta_left_notch === true
                ? 'present'
                : sr.uta_left_notch === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.funneling}
            onChange={(e) =>
              onUpdateFindings?.({
                uta_left_notch: e.target.value === 'present' ? true : false,
              })
            }
          />
        </Field>
      </div>

      <SectionHeading>sections.mcaDoppler</SectionHeading>
      <div className="grid grid-cols-4 gap-1">
        <Field label="fields.pi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'mca_pi')}
            onBlur={blurHandler('mca_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.percentile">
          <NumberInput
            step={1}
            defaultValue={numVal(m, 'mca_pi_percentile')}
            onBlur={blurHandler('mca_pi_percentile', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.vmax">
          <NumberInput
            defaultValue={numVal(m, 'mca_vmax_cms')}
            onBlur={blurHandler('mca_vmax_cms', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.mom">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'mca_vmax_mom')}
            onBlur={blurHandler('mca_vmax_mom', onUpdateFindings)}
          />
        </Field>
      </div>
      <div className="flex items-center gap-2 pt-0.5">
        <span className={styles.label}>{t('fields.cpr')}</span>
        <NumberInput
          step={0.01}
          className="w-16"
          defaultValue={numVal(m, 'cpr')}
          onBlur={blurHandler('cpr', onUpdateFindings)}
        />
        <NumberInput
          step={1}
          className="w-14"
          placeholder="%ile"
          defaultValue={numVal(m, 'cpr_percentile')}
          onBlur={blurHandler('cpr_percentile', onUpdateFindings)}
        />
      </div>

      <SectionHeading>sections.ductusVenosusDoppler</SectionHeading>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.pi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'dv_pi')}
            onBlur={blurHandler('dv_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.percentile">
          <NumberInput
            step={1}
            defaultValue={numVal(m, 'dv_pi_percentile')}
            onBlur={blurHandler('dv_pi_percentile', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.aWave">
          <MiniSelect
            defaultValue={(sr.dv_a_wave as string) || ''}
            options={OPTIONS.aWave}
            onChange={(e) =>
              onUpdateFindings?.({ dv_a_wave: e.target.value || null })
            }
          />
        </Field>
      </div>
    </>
  );
}

function MorphologySection() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.morphologyCneof</SectionHeading>
      <MorphologyList items={T2_MORPHOLOGY} />
      <div className="grid grid-cols-2 gap-1 pt-1">
        <Field label="fields.lemonBanana">
          <MiniSelect
            defaultValue={(sr.lemon_banana_sign as string) || ''}
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'lemon', label: 'options.lemonOnly' },
              { value: 'banana', label: 'options.bananaOnly' },
              { value: 'both', label: 'options.bothOpenNtd' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ lemon_banana_sign: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.doubleBubble">
          <MiniSelect
            defaultValue={(sr.double_bubble_sign as string) || ''}
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'present', label: 'options.presentDuodenalAtresia' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                double_bubble_sign: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.keyholeSign">
          <MiniSelect
            defaultValue={(sr.keyhole_sign as string) || ''}
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'present', label: 'options.presentPuv' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ keyhole_sign: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.bladderCycling">
          <MiniSelect
            defaultValue={(sr.bladder_cycling as string) || ''}
            options={[
              { value: 'normal', label: 'options.normal30min' },
              { value: 'persistent', label: 'options.persistentFull' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ bladder_cycling: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.renalPelvisAp">
          <NumberInput
            placeholder="<0.7"
            defaultValue={numVal(m, 'renal_pelvis_ap_cm')}
            onBlur={blurHandler('renal_pelvis_ap_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.bladderDiam" explainMeasKey="MEAS__BLADDER_DIAMETER">
          <NumberInput
            placeholder="1.5-5.0"
            step={0.1}
            defaultValue={numVal(m, 'bladder_diameter_cm')}
            onBlur={blurHandler('bladder_diameter_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="Transversal Diam. (cm)" explainMeasKey="MEAS__TRANSVERSAL">
          <NumberInput
            placeholder="<3.0"
            step={0.1}
            defaultValue={numVal(m, 'transversal_cm')}
            onBlur={blurHandler('transversal_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.orbitalDiam" explainMeasKey="MEAS__ORBITAL_DIAMETER">
          <NumberInput
            placeholder="1.2-1.5"
            defaultValue={numVal(m, 'orbital_diameter_cm')}
            onBlur={blurHandler('orbital_diameter_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.lhr">
          <NumberInput
            placeholder="1.2-1.4"
            step={0.01}
            defaultValue={numVal(m, 'lhr')}
            onBlur={blurHandler('lhr', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.hepaticHerniation">
          <MiniSelect
            defaultValue={(sr.hepatic_herniation as string) || ''}
            options={[
              { value: 'normal', label: 'options.normalBelowDiaphragm' },
              { value: 'liver-up', label: 'options.liverUpCdh' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                hepatic_herniation: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function CardiacSection() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.cardiac5Views</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field label="fields.cardiacSitus">
          <MiniSelect
            defaultValue={(sr.cardiac_situs as string) || ''}
            options={[
              { value: 'LEVOCARDIA', label: 'options.levocardia' },
              { value: 'DEXTROCARDIA', label: 'options.dextrocardia' },
              { value: 'MESOCARDIA', label: 'options.mesocardia' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ cardiac_situs: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.fourChamber">
          <MiniSelect
            defaultValue={(sr.four_chamber as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABNORMAL', label: 'options.abnormal' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ four_chamber: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.ctr" explainMeasKey="MEAS__CTR">
          <NumberInput
            placeholder="~0.33"
            step={0.01}
            defaultValue={numVal(m, 'ctr')}
            onBlur={blurHandler('ctr', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.lvRvRatio">
          <NumberInput
            placeholder="~1.0"
            step={0.01}
            defaultValue={numVal(m, 'lv_rv_ratio')}
            onBlur={blurHandler('lv_rv_ratio', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.ivs" explainMeasKey="MEAS__IVS_THICKNESS">
          <NumberInput
            placeholder="~0.4"
            defaultValue={numVal(m, 'ivs_thickness_cm')}
            onBlur={blurHandler('ivs_thickness_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.ias">
          <MiniSelect
            defaultValue={(sr.ias as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABNORMAL', label: 'options.abnormal' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ ias: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.mitralValve">
          <MiniSelect
            defaultValue={(sr.mitral_valve as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABNORMAL', label: 'options.abnormal' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ mitral_valve: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.tricuspidValve">
          <MiniSelect
            defaultValue={(sr.tricuspid_valve as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABNORMAL', label: 'options.abnormal' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ tricuspid_valve: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.tricuspidRegurg">
          <MiniSelect
            defaultValue={
              sr.tricuspid_regurgitation === true
                ? 'present'
                : sr.tricuspid_regurgitation === false
                  ? 'absent'
                  : ''
            }
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'present', label: 'options.present' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                tricuspid_regurgitation: e.target.value === 'present',
              })
            }
          />
        </Field>
        <Field label="fields.tr">
          <NumberInput
            step={1}
            defaultValue={
              sr.tricuspid_regurg_vel_cms != null
                ? Number(sr.tricuspid_regurg_vel_cms)
                : undefined
            }
            onBlur={blurHandler('tricuspid_regurg_vel_cms', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.descendingAorta">
          <MiniSelect
            defaultValue={(sr.descending_aorta as string) || ''}
            options={[
              { value: 'normal', label: 'options.singleLeftPost' },
              { value: 'double', label: 'options.double' },
              { value: 'right', label: 'options.rightSided' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                descending_aorta: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-1 pt-0.5">
        <Field label="fields.aorta" explainMeasKey="MEAS__AORTA_DIAMETER">
          <NumberInput
            placeholder="~0.45"
            defaultValue={numVal(m, 'aorta_diameter_cm')}
            onBlur={blurHandler('aorta_diameter_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.lvDiam" explainMeasKey="MEAS__LV_DIAMETER">
          <NumberInput
            step={0.1}
            defaultValue={numVal(m, 'lv_diameter_cm')}
            onBlur={blurHandler('lv_diameter_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.mAoContinuity">
          <MiniSelect
            defaultValue={(sr.mitral_aortic_continuity as string) || ''}
            options={[
              { value: 'PRESENT', label: 'options.present' },
              { value: 'ABSENT', label: 'options.absent' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                mitral_aortic_continuity: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.lvAoAlignment">
          <MiniSelect
            defaultValue={(sr.lvot as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABNORMAL', label: 'options.abnormal' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ lvot: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.pa" explainMeasKey="MEAS__PA_DIAMETER">
          <NumberInput
            placeholder="~0.45"
            defaultValue={numVal(m, 'pa_diameter_cm')}
            onBlur={blurHandler('pa_diameter_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.rvDiam" explainMeasKey="MEAS__RV_DIAMETER">
          <NumberInput
            step={0.1}
            defaultValue={numVal(m, 'rv_diameter_cm')}
            onBlur={blurHandler('rv_diameter_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.greatVesselCross">
          <MiniSelect
            defaultValue={(sr.great_vessel_crossing as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABSENT', label: 'options.absent' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                great_vessel_crossing: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.svc">
          <MiniSelect
            defaultValue={(sr.svc as string) || ''}
            options={[
              { value: 'SINGLE', label: 'options.singleNormal' },
              { value: 'BILATERAL', label: 'options.bilateral' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ svc: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.threeVAlignment">
          <MiniSelect
            defaultValue={(sr.three_vessel_alignment as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal' },
              { value: 'ABNORMAL', label: 'options.abnormal' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                three_vessel_alignment: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.aorticArch">
          <MiniSelect
            defaultValue={(sr.aortic_arch_laterality as string) || ''}
            options={[
              { value: 'LEFT', label: 'options.leftSidedNormal' },
              { value: 'RIGHT', label: 'options.rightDiGeorge' },
              { value: 'NOT_VISUALIZED', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                aortic_arch_laterality: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.ductusArteriosus">
          <MiniSelect
            defaultValue={(sr.ductus_arteriosus as string) || ''}
            options={[
              { value: 'PRESENT', label: 'options.present' },
              { value: 'ABSENT', label: 'options.absent' },
              { value: 'PREMATURE_CLOSURE', label: 'options.prematureClosure' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ ductus_arteriosus: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.vSign">
          <MiniSelect
            defaultValue={(sr.v_sign as string) || ''}
            options={[
              { value: 'v-shape', label: 'options.vShapeNormal' },
              { value: 'straight', label: 'options.straightTga' },
              { value: 'reverse', label: 'options.reverseV' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ v_sign: e.target.value || null })
            }
          />
        </Field>
      </div>
    </>
  );
}

function CervixSection() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.cervix</SectionHeading>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.lengthTvs" explainMeasKey="MEAS__CERVICAL_LENGTH">
          <NumberInput
            placeholder=">=3.5"
            step={0.1}
            defaultValue={numVal(m, 'cervical_length_cm')}
            onBlur={blurHandler('cervical_length_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.internalOs">
          <MiniSelect
            defaultValue={(sr.internal_os as string) || ''}
            options={[
              { value: 'CLOSED', label: 'options.closed' },
              { value: 'OPEN', label: 'options.open' },
              { value: 'FUNNELED', label: 'options.funneled' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ internal_os: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.funneling">
          <MiniSelect
            defaultValue={
              sr.funneling === true
                ? 'present'
                : sr.funneling === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.funneling}
            onChange={(e) =>
              onUpdateFindings?.({
                funneling: e.target.value === 'present' ? true : false,
              })
            }
          />
        </Field>
        <Field label="fields.funnelingPct">
          <NumberInput
            placeholder="%"
            min={0}
            max={100}
            step={1}
            defaultValue={numVal(m, 'funneling_pct')}
            onBlur={blurHandler('funneling_pct', onUpdateFindings)}
          />
        </Field>
      </div>
    </>
  );
}

function PlacentaSection() {
  const {
    fetalAssessment,
    studyReportFields,
    measurementValues,
    onUpdateFindings,
  } = useReportData();
  const sr = studyReportFields;
  const m = measurementValues;
  const mvpCm = numVal(m, 'mvp_cm');
  const calculatedAmnioticFluid = amnioticFluidFromSdpCm(mvpCm);
  const amnioticFluidValue =
    calculatedAmnioticFluid || (sr.fluid_volume as string) || '';

  useEffect(() => {
    if (
      !calculatedAmnioticFluid ||
      calculatedAmnioticFluid === sr.fluid_volume
    ) {
      return;
    }

    void onUpdateFindings?.({ fluid_volume: calculatedAmnioticFluid });
  }, [calculatedAmnioticFluid, onUpdateFindings, sr.fluid_volume]);

  const handleMvpBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value ? parseFloat(e.target.value) : null;
    onUpdateFindings?.({
      mvp_cm: value,
      fluid_volume: amnioticFluidFromSdpCm(value) || null,
    });
  };

  return (
    <>
      <SectionHeading>sections.placentaCordFluid</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field
          label="fields.placentalLocation"
          explainFindingKey="placenta_localization"
        >
          <MiniSelect
            defaultValue={fetalAssessment.placentaLocalization || ''}
            options={OPTIONS.placentalLocation}
            onChange={(e) =>
              onUpdateFindings?.({
                placenta_localization: e.target.value || null,
              })
            }
          />
        </Field>
        <Field
          label="fields.placentaCervixClass"
          explainFindingKey="placenta_distance_to_cervix"
        >
          <MiniSelect
            defaultValue={fetalAssessment.placentaCervixClass || ''}
            options={OPTIONS.placentaCervixClass}
            onChange={(e) =>
              onUpdateFindings?.({
                placenta_distance_to_cervix_class: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.grannumGrade">
          <MiniSelect
            defaultValue={
              sr.grannum_grade != null ? String(sr.grannum_grade) : ''
            }
            options={OPTIONS.grannumGrade}
            onChange={(e) =>
              onUpdateFindings?.({
                grannum_grade: e.target.value ? parseInt(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="fields.thickness" explainMeasKey="MEAS__PLACENTAL_THICKNESS">
          <NumberInput
            placeholder="<4.0"
            step={0.1}
            defaultValue={numVal(m, 'placental_thickness_cm')}
            onBlur={blurHandler('placental_thickness_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.cordVessels">
          <MiniSelect
            defaultValue={(sr.cord_vessels as string) || ''}
            options={OPTIONS.cordVessels}
            onChange={(e) =>
              onUpdateFindings?.({ cord_vessels: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.cordInsertion">
          <MiniSelect
            defaultValue={(sr.cord_insertion as string) || ''}
            options={[
              { value: 'central', label: 'options.central' },
              { value: 'marginal', label: 'options.marginal' },
              { value: 'velamentous', label: 'options.velamentous' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ cord_insertion: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.vasaPraevia">
          <MiniSelect
            defaultValue={
              sr.vasa_praevia === true
                ? 'present'
                : sr.vasa_praevia === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.funneling}
            onChange={(e) =>
              onUpdateFindings?.({
                vasa_praevia: e.target.value === 'absent' ? false : true,
              })
            }
          />
        </Field>
        <Field label="fields.amnioticFluid">
          <MiniSelect
            value={amnioticFluidValue}
            options={OPTIONS.amnioticFluid}
            disabled
          />
        </Field>
        <Field label="fields.afi">
          <NumberInput
            placeholder="5-25"
            step={0.5}
            defaultValue={numVal(m, 'afi_cm')}
            onBlur={blurHandler('afi_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.mvp" explainMeasKey="MEAS__SDP">
          <NumberInput
            placeholder="2-8"
            defaultValue={mvpCm}
            onBlur={handleMvpBlur}
          />
        </Field>
      </div>
    </>
  );
}

export function T2Form() {
  const { reportDetailMode } = useReportData();
  const isFullReport = reportDetailMode === 'full';

  if (!isFullReport) {
    return (
      <TwoColumnLayout
        left={
          <>
            <PresentationVitality />
            <BiometrySection />
            <EFWSection />
          </>
        }
        right={
          <>
            <PlacentaSection />
            <FetalAnatomySection />
            <ScreeningSection />
          </>
        }
      />
    );
  }

  return (
    <TwoColumnLayout
      left={
        <>
          <PresentationVitality />
          <BiometrySection />
          <EFWSection />
          {/* <MorphologySection /> */}
          <DopplerSections />
        </>
      }
      right={
        <>
          <PlacentaSection />
          <FetalAnatomySection />
          <BrainMeasurements />
          <CardiacSection />
          <CervixSection />
          <ScreeningSection />
        </>
      }
    />
  );
}
